import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingSunningdalePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Sunningdale Professional Painters - Residential & Commercial Painting | Estate Specialists',
    description: 'Expert painting services in Sunningdale, Parklands, and Table View. Specialized in residential, commercial, and estate painting. Over 14 years serving the West Coast with quality craftsmanship.',
    keywords: 'painters Sunningdale, house painters Sunningdale, residential painting Sunningdale, commercial painting, estate painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Sunningdale',
          description: 'Professional residential and estate painting',
          address: 'Sunningdale, Cape Town',
          priceRange: '$$',
          serviceArea: ['Sunningdale', 'Parklands', 'Table View', 'Big Bay', 'West Beach', 'Bloubergstrand']
        }),
        generateServiceSchema({
          name: 'Residential & Estate Painting',
          description: 'Complete painting solutions for homes and estates',
          provider: 'Painters Sunningdale',
          areaServed: ['Sunningdale', 'Parklands', 'Table View'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Estate Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Sunningdale",
    location: "Sunningdale, Cape Town",
    website: "https://painterssunningdale.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/2c9df6732ae636ac9627f89359fd99d2.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20modern%20house%20exterior%20with%20white%20walls%20and%20dark%20trim%20professional%20finish%20in%20coastal%20suburban%20residential%20area%20with%20clear%20blue%20sky&width=800&height=600&seq=gallery-sunningdale1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20progress%20with%20professional%20painter%20applying%20paint%20to%20walls%20using%20roller%20clean%20workspace%20with%20drop%20cloths%20and%20painting%20supplies&width=800&height=600&seq=gallery-sunningdale2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20building%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20large%20structure%20modern%20architecture&width=800&height=600&seq=gallery-sunningdale3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20interior%20living%20room%20with%20feature%20wall%20elegant%20color%20scheme%20modern%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=gallery-sunningdale4&orientation=landscape"
    ],
    services: [
      {
        title: "Interior Painting",
        description: "Transform your interior spaces with our expert painting services. We handle everything from single rooms to entire homes, delivering flawless finishes that enhance your living environment.",
        features: ["Living rooms & bedrooms", "Kitchens & bathrooms", "Ceilings & cornices", "Feature walls & accent colors"]
      },
      {
        title: "Exterior Painting",
        description: "Protect and beautify your property's exterior with our professional painting solutions. We use premium weather-resistant paints designed for coastal South African conditions.",
        features: ["House facades", "Boundary walls", "Garage doors", "Window frames & trim"]
      },
      {
        title: "Roof Coating",
        description: "Extend the life of your roof with our specialized roof painting and coating services. We provide waterproofing and heat-reflective solutions for all roof types.",
        features: ["Tile roof painting", "Metal roof coating", "Waterproofing treatments", "Heat-reflective coatings"]
      }
    ],
    serviceAreas: ["Sunningdale", "Parklands", "Table View", "Bloubergstrand", "West Beach", "Big Bay"],
    about: "Painters Sunningdale is a leading painting company serving Sunningdale and the surrounding Northern Suburbs of Cape Town. With over 13 years of experience in the painting industry, we have established ourselves as one of the most trusted and reliable painting contractors in the coastal areas. Our team of skilled professionals is dedicated to transforming your property with precision, care, and attention to detail.",
    aboutExtended: [
      "We specialize in both residential and commercial painting projects, offering comprehensive solutions that cover everything from initial color consultation to final finishing touches. Our expertise extends across interior painting, exterior painting, roof coating, waterproofing, and specialized decorative finishes tailored for coastal environments.",
      "What sets us apart is our commitment to using only premium quality paints and materials from leading brands, ensuring long-lasting results that withstand South Africa's diverse climate conditions, especially the harsh coastal weather. Our painters are fully trained, certified, and insured, giving you complete peace of mind throughout your project.",
      "We understand that every property is unique, which is why we take the time to understand your specific needs, preferences, and budget constraints. Whether you're looking to refresh a single room or undertake a complete property makeover, we deliver exceptional results that exceed expectations."
    ],
    ourApproach: [
      "Detailed consultation and site assessment",
      "Comprehensive surface preparation and repairs",
      "Premium quality paints and materials",
      "Professional application techniques",
      "Thorough cleanup and final inspection",
      "Post-project support and warranty"
    ],
    whyChoose: [
      "13+ years of industry experience",
      "Fully licensed and insured",
      "Free color consultation",
      "Quality materials and paints",
      "Competitive pricing",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Karen Smith",
        rating: 5,
        text: "Excellent service from start to finish! The team was professional, efficient, and the quality of work is outstanding. Our home looks amazing!"
      },
      {
        name: "Robert Johnson",
        rating: 5,
        text: "Painters Sunningdale did a fantastic job on our property. They were punctual, clean, and the results are stunning. Highly recommended!"
      },
      {
        name: "Susan van Zyl",
        rating: 5,
        text: "Very impressed with the professionalism and attention to detail. Great communication throughout the project and very reasonable prices."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Sunningdale</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Sunningdale</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <h4 className="text-xl font-semibold mb-4">
                    <a href="#service-areas" className="hover:text-[#e9f638] transition">
                      We proudly serve the following areas in Cape Town's Northern Suburbs
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of painting services tailored to meet your specific needs and requirements.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Sunningdale, Parklands, Table View</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
